import { Component, inject } from '@angular/core';

import { finalize } from 'rxjs';

import { ProgressSpinnerModule } from 'primeng/progressspinner';

// Composants
import { PageBaseComponent } from 'src/app/@shared/components/abstract/page-base.component';
import { MessageAction, MessageComponent } from 'src/app/@shared/components/message/message.component';

// Services
import { AuthenticationService } from 'src/app/@core/authentication/services/authentication.service';

// Utils
import { ODataMessage } from 'src/app/@shared/utilities/odata/message';

@Component({
    selector: 'app-verify-email',
    imports: [
        MessageComponent,
        ProgressSpinnerModule,
    ],
    templateUrl: './verify-email.component.html',
    styleUrls: ['./verify-email.component.scss']
})
export class VerifyEmailComponent extends PageBaseComponent {
    private _authenticationService = inject(AuthenticationService);

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    message: string = '';

    /**
     * Motif du refus, tel que le serveur le nomme (#1124) :
     * `EMAIL_VERIFICATION_LINK_EXPIRED`, `EMAIL_VERIFICATION_LINK_INVALID`,
     * `EMAIL_VERIFICATION_LINK_INCOMPLETE`, `EMAIL_VERIFICATION_USER_NOT_FOUND` ou
     * `EMAIL_VERIFICATION_ALREADY_REGISTERED`. On affiche le texte du référentiel sans le
     * recopier ici : les cinq ramènent au profil, mais le geste qui y attend diffère —
     * redemander le lien pour quatre d'entre eux, choisir une AUTRE adresse pour le
     * dernier, qui n'est pas un problème de lien. Recopier les libellés ferait disparaître
     * cette nuance au premier copier-coller.
     */
    public refusal: ODataMessage | null = null;

    /**
     * Repli quand le refus n'est pas nommé : réponse sans `messages`, erreur serveur ou
     * coupure réseau. L'écran ne doit jamais rester nu — c'est le symptôme d'origine du
     * ticket, le logo seul et indéfiniment.
     */
    private readonly _unknownRefusal: ODataMessage = new ODataMessage({
        domain: 'email_verification',
        longText: `La validation de votre nouvelle adresse email n'a pas abouti. Rouvrez le lien reçu par email dans `
            + `quelques instants, ou reprenez le changement d'adresse depuis votre profil.`,
        shortText: 'Validation impossible',
        type: 'E'
    });

    /** Sortie commune aux cinq refus : le bloc « Email » du profil, qui renvoie un lien. */
    public readonly profileAction: MessageAction = {
        label: `Aller à mon profil`,
        handler: () => this._goToProfile(),
    };

    /**
     * Le lien s'ouvre souvent dans un autre navigateur que celui de la session, donc sans
     * être connecté — et `EMAIL_VERIFICATION_USER_NOT_FOUND` le dit explicitement. Sans
     * cette seconde sortie, le bouton du profil se contenterait de rebondir sur la garde
     * de route, sans que l'écran ait jamais nommé la connexion.
     */
    public readonly loginActions: MessageAction[] = [{
        label: `Revenir à la connexion`,
        handler: () => this._goToLogin(),
    }];

    override ngOnInit(): void {
        super.ngOnInit();

        this._verifyEmail();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _goToLogin(): void {
        this.router.navigate(['/login']);
    }

    private _goToProfile(): void {
        this.router.navigate(['/user/profile']);
    }

    private _verifyEmail(): void {
        let payload = {
            'email': this.queryParams.get('email'),
            'user_id': this.queryParams.get('user_id'),
            'timestamp': this.queryParams.get('timestamp'),
            'signature': this.queryParams.get('signature')
        };

        this.loading = true;
        this.refusal = null;

        this._authenticationService.verifyEmail$(payload)
            .pipe(
                finalize(() => {
                    this.loading = false;
                })
            )
            .subscribe({
                next: () => {
                    this.message = `Votre email a été validé. Vous allez être redirigé  dans 5 secondes.`;
                    this.userService.loadUserData();
                    setTimeout(() => {
                        this.router.navigate(['/user/profile'])
                    }, 5000);
                },
                error: (error: any) => {
                    this.refusal = error?.messages?.[0] ?? this._unknownRefusal;
                },
            });
    }

}
